/**
 * Phase 45 Plan 45-03 (D-02) — reassign / drop assignee mutation hook.
 *
 * Petra UAT 2026-05-19 — wired from TaskDetailSlideOver's assignee picker
 * for admin / PM personas. Backend gates: admin can override anywhere,
 * PMs + ProjectAssignment holders can reassign within their projects;
 * 403 surfaces as a toast on the calling component.
 *
 * Pass `assigneeId = null` to drop (unassign). Pass a number to assign
 * to that user. Invalidates the board (`['board', projectId]`), MyTasks
 * (`['tasks', 'mine']`) and the unassigned feed (`['tasks', 'unassigned']`)
 * so every surface that mirrors the assignee refreshes after success.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { patchTaskAssignee } from '../services/api/tasks';

export interface UseReassignTaskInput {
  taskId: number;
  assigneeId: number | null;
  projectId: number;
  /** Phase 66 EAP-06 — kind of assignee being set. Defaults to 'user' for back-compat. */
  assigneeKind?: 'user' | 'external_user';
  /** Phase 66 EAP-06 — external user ID when assigneeKind='external_user'. */
  externalAssigneeId?: number | null;
}

export function useReassignTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskId,
      assigneeId,
      assigneeKind,
      externalAssigneeId,
    }: UseReassignTaskInput) =>
      patchTaskAssignee(taskId, assigneeId, { assigneeKind, externalAssigneeId }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['board', String(vars.projectId)] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'mine'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'unassigned'] });
      // 260806-ddg — assigning auto-subscribes the assignee as a watcher
      // (backend side effect); prefix-match hits both audience variants.
      queryClient.invalidateQueries({ queryKey: ['tasks', vars.taskId, 'watchers'] });
    },
  });
}
